import Appointment from '@spike/appointment-model';
import Booking from '@spike/booking-model';
import { AppointmentsState, AppointmentsStatus, countRecurrentAppointmentsThunk } from '@spike/bookings-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import CancellationModal from './V2/CancellationModal/CancellationModal';
import { makeStyles, RadioGroup, Theme } from '@material-ui/core';
import { Radio } from '@material-ui/core';
import { FormControlLabel } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import { useMasterData } from 'hooks';

interface PopUpCancelAppointmentProps {
    loading?: boolean;
    appointmentId: number;
    recurringUuid?: string;
    customerFirstName: string;
    customerLastName: string;
    initCancelationReasonId?: number;
    initSendNotification?: boolean;
    zIndex?: number;
    onClose: () => void;
    onConfirmCancel: (appointmentId?: number, sendNotificacion?: boolean, cancelationReasonId?: number) => void;
    onConfirmCancelRecurrent: (appointmentId: number, sendNotificacion: boolean, cancelationReasonId?: number) => void;
}

export const reasonNoShowId = 4;

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        paddingLeft: 31,
        marginBottom: 20,
        gap: 10,
        [theme.breakpoints.down('md')]: {}
    },
    radioText: {
        fontSize: 14,
        [theme.breakpoints.down('md')]: {
            fontSize: 14
        }
    },
    radioItem: {
        color: '#000'
    },
    radio: {
        'color': '#000',
        'alignItems': 'self-start',
        'gap': 5,
        '& .MuiRadio-colorSecondary': {
            'padding': 0,
            '&.Mui-checked': {
                color: '#222'
            }
        },
        '& .MuiRadio-colorSecondary > .MuiTypography-root:last-child > .MuiTypography-root': {
            paddingTop: 5
        }
    }
}));

export const PopUpCancelAppointment: FunctionComponent<PopUpCancelAppointmentProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const masterdata = useMasterData();

    const appointmentState = useSelector<RootState, AppointmentsState>(state => state.appointments);

    const [appointmentId, setAppointmentId] = useState<number>();
    const [bookingId, setBookingId] = useState<number | undefined>();
    const [remaining, setRemaining] = useState<number>();
    const [showLoading, setShowLoading] = useState<boolean>(props.loading || true);
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [selectedReason, setSelectedReason] = useState(props.initCancelationReasonId);
    const [value, setValue] = useState<string>('only');
    const [sendNotification, setSendNotification] = useState(props.initSendNotification ?? true);

    useEffect(() => {
        setAppointmentId(props.appointmentId);
        if (props.recurringUuid) {
            dispatch(countRecurrentAppointmentsThunk(apiClientWrapper, props.appointmentId));
        } else {
            setShowLoading(false);
        }
    }, []);

    useNonInitialEffect(() => {
        switch (appointmentState.status) {
            case AppointmentsStatus.CountRecurrentSuccess:
                setRemaining(appointmentState.recurrent?.remaining);
                setShowLoading(false);
                setShowOptions(true);
                break;
            default:
                break;
        }
    }, [appointmentState]);

    const confirmCancelHandler = () => {
        if (!showOptions) {
            props.onConfirmCancel(appointmentId, sendNotification, selectedReason);
        } else {
            value === 'only'
                ? props.onConfirmCancel(appointmentId, sendNotification, selectedReason)
                : props.onConfirmCancelRecurrent(appointmentId!, sendNotification, selectedReason);
        }
    };

    const closeHandler = () => {
        props.onClose();
    };

    const slot = (
        <RadioGroup
            aria-label="appointments"
            value={value}
            name="appointments"
            className={classes.container}
            onChange={(_event, value) => setValue(value)}
        >
            <FormControlLabel
                className={classes.radio}
                value={'only'}
                control={<Radio className={classes.radioItem} />}
                key={'only'}
                label={<Typography className={classes.radioText}>{'Only cancel this appointment'}</Typography>}
            />
            <FormControlLabel
                className={classes.radio}
                value={'all'}
                control={<Radio className={classes.radioItem} />}
                key={'all'}
                label={
                    <Typography
                        className={classes.radioText}
                    >{`Cancel ${remaining} upcoming recurring appointments`}</Typography>
                }
            />
        </RadioGroup>
    );

    const CANCEL_QUESTION = 'Are you sure you want to cancel this appointment?';

    const CANCEL_TITLE = 'Cancel Appointment';

    const CHECKBOX_LABEL = `Send a notification to ${
        props.customerFirstName + ' ' + props.customerLastName
    } that their appointment has been canceled.`;

    return (
        <CancellationModal
            loading={showLoading}
            title={CANCEL_TITLE}
            checkboxLabel={CHECKBOX_LABEL}
            question={CANCEL_QUESTION}
            reasons={masterdata.cancelationReasons}
            initReasonId={selectedReason}
            isOpen={true}
            checked={sendNotification}
            slot={showOptions && slot}
            zIndex={props.zIndex}
            onSelectReason={selectedField => {
                if (selectedField) {
                    setSelectedReason(Number(selectedField.id));
                }
            }}
            onChecked={setSendNotification}
            onClose={closeHandler}
            onConfirm={confirmCancelHandler}
        />
    );
};

export default PopUpCancelAppointment;
